/**
 * The links data layer's typed calls (1.J deliverable 1): one function per consumed route over an
 * `ApiClient`. `LinksApiContext` holds the real one so browser-unit specs pass a fake.
 */
import {
  type BacklinksV1,
  type CreatedDocumentV1,
  type DocMetaV1,
  type DocTitleV1,
  docsCreate,
  docsMeta,
  docsTitles,
  type LinkCreateResultV1,
  type LinkCreateV1,
  type LinkDirection,
  type LinkReorderV1,
  type LinkTypeV1,
  type LinkUpdateV1,
  type LinkV1,
  linksBacklinks,
  linksCreate,
  linksList,
  linksRemove,
  linksReorder,
  linksTree,
  linksTypes,
  linksUpdate,
  type MentionLinkRequestV1,
  type MentionLinkResponseV1,
  mentionsLink,
  type PickerResultV1,
  searchPicker,
  searchUnlinked,
  type TreeNodeV1,
  type UnlinkedOccurrenceV1,
} from '@codex/contracts';
import { type ApiClient, api as defaultApi } from '@codex/contracts/client';
import { createContext, useContext } from 'react';
import { TITLES_PER_REQUEST } from '../editor-services/adapter.js';
import { undoable } from '../undo/undoable.js';

export interface ListOptions {
  readonly direction?: LinkDirection | undefined;
  readonly linkTypeKey?: string | undefined;
}

export interface PickerOptions {
  readonly q: string;
  readonly limit?: number;
  readonly capability?: 'structural';
}

export interface LinksApi {
  list(docId: string, options?: ListOptions): Promise<LinkV1[]>;
  create(docId: string, body: LinkCreateV1): Promise<LinkCreateResultV1>;
  update(docId: string, linkId: string, body: LinkUpdateV1): Promise<LinkV1>;
  remove(docId: string, linkId: string, baseRv: number): Promise<void>;
  reorder(docId: string, body: LinkReorderV1): Promise<LinkV1>;
  types(): Promise<LinkTypeV1[]>;
  tree(parentId: string | null): Promise<TreeNodeV1[]>;
  titles(ids: readonly string[]): Promise<DocTitleV1[]>;
  meta(docId: string): Promise<DocMetaV1>;
  picker(options: PickerOptions, signal?: AbortSignal): Promise<PickerResultV1>;
  createDoc(typeKey: string, title: string): Promise<CreatedDocumentV1>;
  backlinks(docId: string): Promise<BacklinksV1>;
  unlinked(
    docId: string,
    cursor?: string | null,
  ): Promise<{ items: UnlinkedOccurrenceV1[]; next: string | null }>;
  mentionLink(docId: string, body: MentionLinkRequestV1): Promise<MentionLinkResponseV1>;
}

export function createLinksApi(client: ApiClient = defaultApi): LinksApi {
  return {
    async list(docId, options = {}) {
      const query = {
        ...(options.direction ? { direction: options.direction } : {}),
        ...(options.linkTypeKey ? { linkTypeKey: options.linkTypeKey } : {}),
      };
      return (await client(linksList, { params: { id: docId }, query })).items;
    },
    create: (docId, body) => client(linksCreate, { params: { id: docId }, body }),
    update: (docId, linkId, body) => client(linksUpdate, { params: { id: docId, linkId }, body }),
    async remove(docId, linkId, baseRv) {
      await client(
        linksRemove,
        { params: { id: docId, linkId }, query: { baseRv } },
        undoable('links.remove'),
      );
    },
    reorder: (docId, body) =>
      client(linksReorder, { params: { id: docId }, body }, undoable('links.reorder')),
    async types() {
      return (await client(linksTypes)).items;
    },
    async tree(parentId) {
      const query = parentId === null ? {} : { parentId };
      return (await client(linksTree, { query })).items;
    },
    async titles(ids) {
      const answers: DocTitleV1[] = [];
      for (let start = 0; start < ids.length; start += TITLES_PER_REQUEST) {
        const batch = ids.slice(start, start + TITLES_PER_REQUEST);
        answers.push(...(await client(docsTitles, { body: { ids: batch } })).titles);
      }
      return answers;
    },
    meta: (docId) => client(docsMeta, { params: { id: docId } }),
    picker(options, signal) {
      const query = {
        q: options.q,
        limit: options.limit ?? 8,
        ...(options.capability ? { capability: options.capability } : {}),
      };
      return client(searchPicker, { query }, signal ? { signal } : {});
    },
    createDoc: (typeKey, title) => client(docsCreate, { body: { typeKey, title } }),
    backlinks: (docId) => client(linksBacklinks, { params: { id: docId } }),
    unlinked: (docId, cursor) =>
      client(searchUnlinked, {
        params: { id: docId },
        query: { limit: 50, ...(cursor ? { cursor } : {}) },
      }),
    mentionLink: (docId, body) => client(mentionsLink, { params: { id: docId }, body }),
  };
}

export const LinksApiContext = createContext<LinksApi>(createLinksApi());

export function useLinksApi(): LinksApi {
  return useContext(LinksApiContext);
}
